import { useState, useEffect, useCallback } from "react";
import {
  DatabaseBackup, Download, RotateCcw, Trash2, ShieldCheck, Play,
  AlertTriangle, CheckCircle, XCircle, Clock, HardDrive, FileJson
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { authFetch } from "../../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const statusBadge = {
  pending: { label: "In attesa", cls: "bg-yellow-500/20 text-yellow-400" },
  executed: { label: "Eseguita", cls: "bg-green-500/20 text-green-400" },
  rejected: { label: "Respinta", cls: "bg-red-500/20 text-red-400" },
};

export default function AdminBackups({ user }) {
  const [backups, setBackups] = useState([]);
  const [gdprRequests, setGdprRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [running, setRunning] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(null); // backup object
  const [restoreScope, setRestoreScope] = useState("all");
  const [restoreMode, setRestoreMode] = useState("missing_only");
  const [restoreUserId, setRestoreUserId] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [bRes, gRes, uRes] = await Promise.all([
        authFetch(`${API}/admin/backups`),
        authFetch(`${API}/admin/gdpr/requests`),
        authFetch(`${API}/admin/users`),
      ]);
      if (bRes.ok) setBackups(await bRes.json());
      if (gRes.ok) setGdprRequests(await gRes.json());
      if (uRes.ok) setUsers(await uRes.json());
    } catch (e) {
      toast.error("Errore nel caricamento");
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runBackup = async () => {
    setRunning(true);
    try {
      const res = await authFetch(`${API}/admin/backups/run`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Backup completato: ${data.total_docs} documenti (${formatBytes(data.size_bytes)})`);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore backup");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setRunning(false);
    }
  };

  const downloadBackup = async (backupId) => {
    try {
      const res = await authFetch(`${API}/admin/backups/${backupId}/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${backupId}.json.gz`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error("Errore nel download");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm("Rimuovere questo backup dall'elenco?")) return;
    const res = await authFetch(`${API}/admin/backups/${backupId}`, { method: "DELETE" });
    if (res.ok) { toast.success("Backup rimosso"); fetchAll(); }
    else toast.error("Errore nella rimozione");
  };

  const executeRestore = async () => {
    if (restoreScope === "user" && !restoreUserId) {
      toast.error("Seleziona l'utente da ripristinare");
      return;
    }
    if (restoreMode === "overwrite" && !window.confirm(
      "ATTENZIONE: la modalità SOVRASCRIVI sostituirà i dati attuali con quelli del backup. Continuare?"
    )) return;
    setRestoring(true);
    try {
      const res = await authFetch(`${API}/admin/backups/${restoreDialog.backup_id}/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: restoreMode,
          scope: restoreScope,
          user_id: restoreScope === "user" ? restoreUserId : null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setRestoreDialog(null);
        fetchAll();
      } else {
        toast.error(data.detail || "Errore nel ripristino");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setRestoring(false);
    }
  };

  const processGdpr = async (requestId, action) => {
    const msg = action === "execute"
      ? "Confermi la CANCELLAZIONE DEFINITIVA dell'account e dei dati personali? I documenti fiscali verranno conservati per obbligo di legge."
      : "Respingere la richiesta di cancellazione?";
    if (!window.confirm(msg)) return;
    const res = await authFetch(`${API}/admin/gdpr/requests/${requestId}/${action}`, { method: "POST" });
    const data = await res.json();
    if (res.ok) { toast.success(data.message || "Operazione completata"); fetchAll(); }
    else toast.error(data.detail || "Errore");
  };

  const pendingGdpr = gdprRequests.filter((r) => r.status === "pending");

  return (
    <div className="space-y-8" data-testid="admin-backups-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <DatabaseBackup className="w-7 h-7 text-primary" />
            Backup & GDPR
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Snapshot automatici giornalieri + backup manuali. Ripristino per singolo utente o completo.
          </p>
        </div>
        <Button data-testid="run-backup-btn" className="btn-primary" onClick={runBackup} disabled={running}>
          <Play className="w-4 h-4 mr-2" />
          {running ? "Backup in corso..." : "Esegui Backup Ora"}
        </Button>
      </div>

      {/* Backups list */}
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
        <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-primary" />
          Snapshot Disponibili ({backups.length})
        </h2>
        {backups.length === 0 ? (
          <div className="text-center text-zinc-500 py-8">
            Nessun backup ancora. Il primo backup automatico verrà creato entro un'ora, oppure eseguine uno manuale.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-left border-b border-zinc-800">
                  <th className="py-2 pr-4">Data</th>
                  <th className="py-2 pr-4">Tipo</th>
                  <th className="py-2 pr-4">Utenti</th>
                  <th className="py-2 pr-4">Documenti</th>
                  <th className="py-2 pr-4">Dimensione</th>
                  <th className="py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((b) => (
                  <tr key={b.backup_id} className="border-b border-zinc-800/50" data-testid={`backup-row-${b.backup_id}`}>
                    <td className="py-3 pr-4 text-white">
                      {new Date(b.created_at).toLocaleString("it-IT")}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs ${b.type === "auto" ? "bg-blue-500/20 text-blue-400" : "bg-primary/20 text-primary"}`}>
                        {b.type === "auto" ? "Automatico" : "Manuale"}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-zinc-300">{b.users_count}</td>
                    <td className="py-3 pr-4 text-zinc-300">{b.total_docs}</td>
                    <td className="py-3 pr-4 text-zinc-300">{formatBytes(b.size_bytes)}</td>
                    <td className="py-3 flex gap-2">
                      <Button size="sm" variant="outline" className="border-zinc-700 h-8"
                        data-testid={`restore-backup-btn-${b.backup_id}`}
                        onClick={() => { setRestoreDialog(b); setRestoreScope("all"); setRestoreMode("missing_only"); setRestoreUserId(""); }}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => downloadBackup(b.backup_id)}
                        data-testid={`download-backup-btn-${b.backup_id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-red-400" onClick={() => deleteBackup(b.backup_id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* GDPR Requests */}
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6" data-testid="gdpr-requests-section">
        <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          Richieste Cancellazione GDPR (Art. 17)
          {pendingGdpr.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full text-xs">{pendingGdpr.length} in attesa</span>
          )}
        </h2>
        {gdprRequests.length === 0 ? (
          <div className="text-center text-zinc-500 py-6">Nessuna richiesta di cancellazione</div>
        ) : (
          <div className="space-y-3">
            {gdprRequests.map((r) => (
              <div key={r.request_id} className="p-4 bg-zinc-800/50 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid={`gdpr-request-${r.request_id}`}>
                <div className="flex-1">
                  <div className="text-white font-medium">{r.company_name || r.username} <span className="text-zinc-500 text-xs">({r.email})</span></div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Richiesta il {new Date(r.created_at).toLocaleString("it-IT")}
                    {r.reason && ` · Motivo: ${r.reason}`}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs ${statusBadge[r.status]?.cls}`}>
                  {statusBadge[r.status]?.label || r.status}
                </span>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-red-600 hover:bg-red-700 h-8"
                      data-testid={`gdpr-execute-btn-${r.request_id}`}
                      onClick={() => processGdpr(r.request_id, "execute")}>
                      <CheckCircle className="w-3.5 h-3.5 mr-1" /> Esegui Cancellazione
                    </Button>
                    <Button size="sm" variant="outline" className="border-zinc-700 h-8"
                      onClick={() => processGdpr(r.request_id, "reject")}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Respingi
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* GDPR Compliance summary */}
      <div className="bg-zinc-900/50 rounded-lg border border-zinc-800 p-6">
        <h2 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
          <FileJson className="w-5 h-5 text-primary" />
          Stato Conformità GDPR
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {[
            ["Password cifrate (bcrypt), nessuna password in chiaro", true],
            ["Diritto alla portabilità (Art. 20): export dati JSON per ogni utente", true],
            ["Diritto alla cancellazione (Art. 17): richiesta + approvazione admin", true],
            ["Conservazione documenti fiscali per obbligo di legge (Art. 17.3.b)", true],
            ["File utente su storage sicuro non pubblico (Object Store)", true],
            ["Audit log delle operazioni amministrative", true],
            ["Backup cifrati in transito, accesso solo admin autenticato", true],
            ["Blocco account dopo tentativi falliti + sessioni revocabili", true],
          ].map(([label]) => (
            <div key={label} className="flex items-start gap-2 text-zinc-300">
              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
              {label}
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-zinc-400 flex items-start gap-2">
          <Clock className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          Le richieste di cancellazione devono essere elaborate entro 30 giorni (Art. 12.3 GDPR). Il documento completo di conformità è disponibile nel file GDPR_COMPLIANCE.md del progetto.
        </div>
      </div>

      {/* Restore Dialog */}
      <Dialog open={!!restoreDialog} onOpenChange={(o) => !o && setRestoreDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary" />
              Ripristina Backup
            </DialogTitle>
          </DialogHeader>
          {restoreDialog && (
            <div className="space-y-4 py-2">
              <div className="text-sm text-zinc-400">
                Snapshot del <span className="text-white">{new Date(restoreDialog.created_at).toLocaleString("it-IT")}</span> · {restoreDialog.total_docs} documenti · {restoreDialog.users_count} utenti
              </div>

              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Ambito</label>
                <Select value={restoreScope} onValueChange={setRestoreScope}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="restore-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="all">Tutti gli utenti e tutti i documenti</SelectItem>
                    <SelectItem value="user">Singolo utente (con tutti i suoi dati)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {restoreScope === "user" && (
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Utente da ripristinare</label>
                  <Select value={restoreUserId} onValueChange={setRestoreUserId}>
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="restore-user-select">
                      <SelectValue placeholder="Seleziona utente" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-64">
                      {users.map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          {u.company_name || u.username} ({u.user_type || u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    Puoi ripristinare anche utenti cancellati, se presenti nel backup selezionato: in tal caso seleziona "Tutti gli utenti" con modalità "Solo dati mancanti".
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Modalità</label>
                <Select value={restoreMode} onValueChange={setRestoreMode}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="restore-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="missing_only">Solo dati mancanti (sicuro, non sovrascrive nulla)</SelectItem>
                    <SelectItem value="overwrite">Sovrascrivi tutto (riporta allo stato del backup)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {restoreMode === "overwrite" && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  I dati attuali verranno sostituiti con quelli del backup. Le modifiche fatte dopo la data del backup andranno perse per i dati sovrascritti.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setRestoreDialog(null)}>Annulla</Button>
            <Button className="btn-primary" onClick={executeRestore} disabled={restoring} data-testid="confirm-restore-btn">
              {restoring ? "Ripristino..." : "Avvia Ripristino"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
